<div class="flex flex-col gap-8 p-6">
  @if (!isSidebarMode) {
    <ep-breadcrumbs>
      <ep-breadcrumb [routerLink]="['/']"><span>Realm</span></ep-breadcrumb>
      <ep-breadcrumb [routerLink]="['/users']"><span>Users</span></ep-breadcrumb>
      <ep-breadcrumb
        ><span>{{ user?.username || 'User' }}</span></ep-breadcrumb
      >
    </ep-breadcrumbs>
  }
  @if (isLoading) {
    <div class="text-center py-8">Loading user details...</div>
  } @else {
    <div class="flex justify-between items-center gap-4">
      <h1 [class.text-lg]="isSidebarMode">
        {{
          user && user.firstName && user.lastName
            ? user.firstName + ' ' + user.lastName
            : user?.username
        }}
      </h1>

      <div class="flex gap-3 flex-1">
        @if (
          user?.attributes?.['last-check-customerno']?.[0] != null &&
          user?.attributes?.['customerno']?.[0] != null
        ) {
          <ep-chip size="small" color="success">Synced customer</ep-chip>
        }

        @if (user?.attributes?.['employee-id']?.[0] != null) {
          <ep-chip size="small" color="primary">Employee</ep-chip>
        }
      </div>

      @if (isSidebarMode) {
        <ep-button type="quiet" (click)="closeSidebar()">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </ep-button>
      }
    </div>

    @if (successMessage) {
      <ep-alert type="toast" variant="alt" color="success" [center]="true" [bottom]="true">
        <span slot="header">{{ successMessage }}</span>
      </ep-alert>
    }
    @if (errorMessage) {
      <ep-alert type="toast" variant="alt" color="danger" [center]="true" [bottom]="true">
        <span slot="header">{{ errorMessage }}</span>
      </ep-alert>
    }

    <ep-tab-container [activeTab]="activeTab" (tab-change)="setActiveTab($event)">
      <ep-tab value="details"><span>Details</span></ep-tab>
      <ep-tab value="roles"><span>Role Mappings</span></ep-tab>
      <ep-tab value="groups"><span>Groups</span></ep-tab>
    </ep-tab-container>

    <!-- Details Tab -->
    @if (activeTab === 'details') {
      @if (canEditUser()) {
        <form [formGroup]="userForm" (ngSubmit)="saveUser()" class="flex flex-col gap-5">
          <ep-textfield label="Username" formControlName="username" ngDefaultControl>
            @if (userForm.get('username')?.invalid && userForm.get('username')?.touched) {
              <div slot="error">Username is required</div>
            }
          </ep-textfield>

          <ep-textfield label="Email" type="email" formControlName="email" ngDefaultControl>
            @if (userForm.get('email')?.invalid && userForm.get('email')?.touched) {
              <div slot="error">Enter a valid email address</div>
            }
          </ep-textfield>

          <ep-textfield
            label="First Name"
            formControlName="firstName"
            ngDefaultControl
          ></ep-textfield>

          <ep-textfield
            label="Last Name"
            formControlName="lastName"
            ngDefaultControl
          ></ep-textfield>

          <ep-checkbox formControlName="enabled" ngDefaultControl> User Enabled </ep-checkbox>

          <ep-checkbox formControlName="emailVerified" ngDefaultControl>
            Email Verified
          </ep-checkbox>

          @if (isExternalUser()) {
            <ep-alert color="info" [center]="true" [bottom]="true">
              <span slot="header"
                >This user is managed by an external identity provider and some fields may not be
                editable.</span
              >
            </ep-alert>
          }

          <div class="flex justify-end">
            <ep-button color="primary" [disabled]="userForm.invalid || isSaving">
              <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                <path
                  d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"
                />
              </svg>
              <span>Save Changes</span>
            </ep-button>
          </div>
        </form>
      } @else {
        <section class="flex flex-col gap-5">
          <div class="flex flex-col">
            <label>Name</label>
            <span>{{ user?.firstName }} {{ user?.lastName }}</span>
          </div>
          <div class="flex flex-col">
            <label>Email</label>
            <span>{{ user?.email || 'No email provided' }}</span>
          </div>
          <div class="flex flex-col">
            <label>Enabled</label>
            <span>{{ user?.enabled ? 'Yes' : 'No' }}</span>
          </div>
          @if (user?.attributes?.['employee-id']?.[0] != null) {
            <div class="flex flex-col">
              <label>Employee country</label>
              <span>{{ user?.attributes?.['employee-country']?.[0] }}</span>
            </div>
          }
          @if (user?.attributes?.['last-check-customerno']?.[0] != null) {
            <div class="flex flex-col">
              <label>Last Salesforce Sync</label>
              <div class="flex items-center gap-2">
                <span class="flex-1"
                  >{{
                    $safeNavigationMigration(user?.attributes?.['last-check-customerno']?.[0])
                      | date: 'short'
                  }}
                  ({{
                    getRelativeTime(
                      $safeNavigationMigration(user?.attributes?.['last-check-customerno']?.[0])
                    )
                  }})</span
                >
                @if (user?.attributes?.['last-check-customerno']?.[0] !== '') {
                  <ep-button
                    type="outline"
                    size="small"
                    (click)="invalidateSalesforceCache()"
                    [disabled]="isSaving"
                    title="Invalidate Salesforce cache to force a fresh sync on next login"
                  >
                    <svg viewBox="0 -960 960 960" class="w-4 h-4">
                      <path
                        d="M440-122q-121-15-200.5-105.5T160-440q0-66 26-126.5T260-672l57 57q-38 34-57.5 79T240-440q0 88 56 155.5T440-202v80Zm80 0v-80q87-16 143.5-83T720-440q0-100-70-170t-170-70h-3l44 44-56 56-140-140 140-140 56 56-44 44h3q134 0 227 93t93 227q0 121-79.5 211.5T520-122Z"
                      />
                    </svg>
                    <span>Invalidate Cache</span>
                  </ep-button>
                }
              </div>
            </div>
          } @else {
            <div class="flex flex-col">
              <span class="body-sm">No active Salesforce cache</span>
              <span class="body-xs text-fg-muted">User will be synced on the next login</span>
            </div>
          }
          @if (
            user?.attributes?.['last-check-customerno']?.[0] == null &&
            user?.attributes?.['customerno']?.[0] == null &&
            user?.attributes?.['employee-id']?.[0] == null &&
            user?.attributes?.['terms_and_conditions']?.[0] == null
          ) {
            <ep-alert color="warning" variant="alt" size="small">
              <svg viewBox="0 -960 960 960" class="icon" slot="prefix">
                <path
                  d="m40-120 440-760 440 760H40Zm138-80h604L480-720 178-200Zm302-40q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Zm40-100Z"
                />
              </svg>
              <span slot="header">User didn't log in yet</span>
            </ep-alert>
          }
          @if (user?.attributes?.['customerno']?.[0] != null) {
            <div class="flex flex-col">
              <label>Customer</label>
              <span
                >{{ user?.attributes?.['customername']?.[0] ?? '' }} ({{
                  user?.attributes?.['customerno']?.[0]
                }}
                )</span
              >
            </div>

            <div class="flex flex-col">
              <label>Customer country</label>
              <span>{{ user?.attributes?.['customercountrycode']?.[0] ?? '' }}</span>
            </div>
          }
        </section>
      }
    }

    <!-- Role Mappings Tab -->
    @if (activeTab === 'roles') {
      <div class="flex flex-col gap-5">
        <ep-select
          (ngModelChange)="onClientChange($event)"
          ngDefaultControl
          [ngModel]="selectedClient"
        >
          @for (client of clients; track client.id) {
            <ep-option [value]="client.id" [label]="client.name || client.clientId">
              <span slot="text">{{ client.name || client.clientId }}</span>
            </ep-option>
          }
        </ep-select>

        @if (isLoadingRoles) {
          <div class="text-center py-4">Loading roles...</div>
        }
        @if (!isLoadingRoles && selectedClient) {
          <div class="grid grid-cols-2 gap-6">
            <!-- Available roles -->
            <div class="flex flex-col gap-3">
              <h3>Available Roles</h3>
              @if (availableRoles.length === 0) {
                <div class="text-center py-4 text-gray-500">No available roles</div>
              } @else {
                <div class="overflow-y-auto max-h-80">
                  @for (role of availableRoles; track role.id) {
                    <ep-item>
                      <span slot="text">{{ role.name }}</span>
                      @if (role.description) {
                        <span slot="subtext">{{ role.description }}</span>
                      }
                      <span slot="extra">
                        <ep-button
                          color="default"
                          size="small"
                          (click)="assignRole(role)"
                          [disabled]="loadingRoleIds.has(role.id!)"
                        >
                          @if (loadingRoleIds.has(role.id!)) {
                            <svg
                              class="icon icon--small animate-spin"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke-width="4"
                                stroke="currentColor"
                                stroke-opacity="0.25"
                              />
                              <path
                                d="M12 2a10 10 0 0 1 10 10"
                                stroke-width="4"
                                stroke="currentColor"
                              />
                            </svg>
                          } @else {
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                            </svg>
                          }
                        </ep-button>
                      </span>
                    </ep-item>
                  }
                </div>
              }
            </div>

            <!-- Assigned roles -->
            <div class="flex flex-col gap-3">
              <h3>Assigned Roles</h3>
              @if (assignedRoles.length === 0) {
                <div class="text-center py-4 text-gray-500">No roles assigned</div>
              } @else {
                <div class="overflow-y-auto max-h-80">
                  @for (role of assignedRoles; track role.id) {
                    <ep-item>
                      <span slot="text">{{ role.name }}</span>
                      @if (role.description) {
                        <span slot="subtext">{{ role.description }}</span>
                      }
                      <span slot="extra">
                        <ep-button
                          color="default"
                          size="small"
                          (click)="removeRole(role)"
                          [disabled]="loadingRoleIds.has(role.id!)"
                        >
                          @if (loadingRoleIds.has(role.id!)) {
                            <svg
                              class="icon icon--small animate-spin"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke-width="4"
                                stroke="currentColor"
                                stroke-opacity="0.25"
                              />
                              <path
                                d="M12 2a10 10 0 0 1 10 10"
                                stroke-width="4"
                                stroke="currentColor"
                              />
                            </svg>
                          } @else {
                            <svg
                              class="icon icon--small"
                              viewBox="0 -960 960 960"
                              fill="currentColor"
                            >
                              <path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z" />
                            </svg>
                          }
                        </ep-button>
                      </span>
                    </ep-item>
                  }
                </div>
              }
            </div>
          </div>
          <div>
            <h3 class="mt-6">Effective Roles</h3>
            <div class="overflow-y-auto max-h-80">
              @if (effectiveRoles.length === 0) {
                <div class="text-center py-4 text-gray-500">No effective roles</div>
              } @else {
                @for (role of effectiveRoles; track role.id) {
                  <ep-item
                    ><span>{{ role.name }}</span>
                    @if (role.description) {
                      <span slot="subtext">({{ role.description }})</span>
                    }
                  </ep-item>
                }
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- Groups Tab -->
    @if (activeTab === 'groups') {
      <div class="flex flex-col gap-5">
        @if (isLoadingGroups) {
          <div class="text-center py-4">Loading groups...</div>
        } @else {
          <div class="grid grid-cols-1 gap-6" [ngClass]="{ 'grid-cols-2': canEditUser() }">
            <!-- Available groups -->
            @if (canEditUser()) {
              <div class="flex flex-col gap-3">
                <h3>Available Groups</h3>
                <ep-textfield
                  placeholder="Search groups..."
                  (input)="onGroupSearchChange($event)"
                ></ep-textfield>
                @if (filteredAvailableGroups.length === 0) {
                  <div class="text-center py-4 text-gray-500">No available groups</div>
                } @else {
                  <div class="overflow-y-auto max-h-80">
                    @for (group of filteredAvailableGroups; track group.id) {
                      <ep-item (click)="group.subGroupCount && toggleGroupExpand(group)">
                        <span slot="text" [class.font-semibold]="group.subGroupCount">
                          {{ group.displayName }}
                        </span>
                        @if (group.subGroupCount) {
                          <span slot="subtext">Contains {{ group.subGroupCount }} subgroups</span>
                        }
                        <span slot="extra">
                          @if (group?.access?.manageMembership) {
                            <ep-button
                              color="default"
                              size="small"
                              (click)="addToGroup(group); $event.stopPropagation()"
                              [disabled]="loadingGroupIds.has(group.id!)"
                            >
                              @if (loadingGroupIds.has(group.id!)) {
                                <svg
                                  class="icon animate-spin"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke-width="4"
                                    stroke="currentColor"
                                    stroke-opacity="0.25"
                                  />
                                  <path
                                    d="M12 2a10 10 0 0 1 10 10"
                                    stroke-width="4"
                                    stroke="currentColor"
                                  />
                                </svg>
                              } @else {
                                <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
                                  <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" />
                                </svg>
                              }
                            </ep-button>
                          }
                        </span>
                      </ep-item>
                    }
                  </div>
                }
              </div>
            }

            <!-- User groups -->
            <div class="flex flex-col gap-3">
              <h3>Group Membership</h3>
              @if (userGroups.length === 0) {
                <div class="text-center py-4 text-gray-500">User is not a member of any groups</div>
              } @else {
                <div class="overflow-y-auto max-h-80">
                  @for (group of userGroups; track group.id) {
                    <ep-item>
                      <span slot="text">{{ group.path || group.name }}</span>
                      <span slot="extra">
                        <span>{{ group?.access }}</span>
                        @if (group?.access?.manageMembership) {
                          <ep-button
                            color="default"
                            size="small"
                            (click)="removeFromGroup(group)"
                            [disabled]="loadingGroupIds.has(group.id!)"
                          >
                            @if (loadingGroupIds.has(group.id!)) {
                              <svg
                                class="icon icon--small animate-spin"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke-width="4"
                                  stroke="currentColor"
                                  stroke-opacity="0.25"
                                />
                                <path
                                  d="M12 2a10 10 0 0 1 10 10"
                                  stroke-width="4"
                                  stroke="currentColor"
                                />
                              </svg>
                            } @else {
                              <svg
                                class="icon icon--small"
                                viewBox="0 -960 960 960"
                                fill="currentColor"
                              >
                                <path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z" />
                              </svg>
                            }
                          </ep-button>
                        }
                      </span>
                    </ep-item>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- Password Reset Modal -->
  <ep-modal [open]="showPasswordModal" (close)="closePasswordModal()">
    <h3 slot="header">Reset Password</h3>
    <form [formGroup]="passwordForm" (ngSubmit)="resetPassword()" class="flex flex-col gap-5">
      <ep-textfield
        label="New Password"
        type="password"
        formControlName="password"
        ngDefaultControl
      >
        @if (passwordForm.get('password')?.invalid && passwordForm.get('password')?.touched) {
          <div slot="error">Password must be at least 8 characters</div>
        }
      </ep-textfield>

      <ep-textfield
        label="Confirm Password"
        type="password"
        formControlName="passwordConfirm"
        ngDefaultControl
      >
        @if (passwordForm.hasError('mismatch') && passwordForm.get('passwordConfirm')?.touched) {
          <div slot="error">Passwords don't match</div>
        }
      </ep-textfield>

      <ep-checkbox formControlName="temporary" ngDefaultControl>
        Require password reset on next login
      </ep-checkbox>

      <div class="flex justify-end gap-2" slot="actions">
        <ep-button color="secondary" (click)="closePasswordModal()"> Cancel </ep-button>
        <ep-button color="primary" [disabled]="passwordForm.invalid || isSaving">
          Reset Password
        </ep-button>
      </div>
    </form>
  </ep-modal>

  <!-- Delete User Modal -->
  <ep-modal [open]="showDeleteModal" (close)="closeDeleteModal()">
    <h3 slot="header">Delete User</h3>
    <div class="flex flex-col gap-4">
      <p>Are you sure you want to delete user "{{ user?.username }}"?</p>
      <p class="text-red-500">This action cannot be undone.</p>
    </div>
    <div class="flex justify-end gap-2" slot="actions">
      <ep-button color="secondary" (click)="closeDeleteModal()"> Cancel </ep-button>
      <ep-button color="danger" (click)="deleteUser()" [disabled]="isSaving">
        Delete User
      </ep-button>
    </div>
  </ep-modal>
</div>
